import React, { useEffect, useRef } from 'react';

const DEG_TO_RAD = Math.PI / 180;

const Utils = {
  parsePx: (value) => parseFloat(value.replace(/px/, '')),
  getRandomInRange: (min, max, precision = 0) => {
    const multiplier = Math.pow(10, precision);
    const randomValue = Math.random() * (max - min) + min;
    return Math.floor(randomValue * multiplier) / multiplier;
  },
  getRandomItem: (array) => array[Math.floor(Math.random() * array.length)],
  getScaleFactor: () => Math.log(window.innerWidth) / Math.log(1920),
  debounce: (func, delay) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), delay);
    };
  },
};

const defaultConfettiConfig = {
  confettiesNumber: 3500,
  confettiRadius: 9,
  confettiColors: [
    '#FFD700',
    '#FF6347',
    '#32CD32',
    '#4169E1',
    '#FF69B4',
    '#8A2BE2',
    '#2184FDCC',
  ],
  emojies: [],
  svgIcon: null,
};

class Confetti {
  constructor({ initialPosition, direction, radius, colors, emojis, svgIcon }) {
    const speedFactor =
      Utils.getRandomInRange(0.9, 1.7, 6) * Utils.getScaleFactor();
    this.speed = { x: speedFactor, y: speedFactor };
    this.finalSpeedX = Utils.getRandomInRange(0.2, 0.6, 6);
    this.rotationSpeed =
      emojis.length || svgIcon
        ? 0.01
        : Utils.getRandomInRange(0.03, 0.07, 3) * Utils.getScaleFactor();
    this.dragCoefficient = Utils.getRandomInRange(0.0005, 0.0009, 6);
    this.radius = { x: radius, y: radius };
    this.initialRadius = radius;
    this.rotationAngle =
      direction === 'left'
        ? Utils.getRandomInRange(0, 0.2, 3)
        : Utils.getRandomInRange(-0.2, 0, 3);
    this.emojiRotationAngle = Utils.getRandomInRange(0, 2 * Math.PI);
    this.radiusYDirection = 'down';

    const angle =
      direction === 'left'
        ? Utils.getRandomInRange(110, 15) * DEG_TO_RAD
        : Utils.getRandomInRange(-15, 110) * DEG_TO_RAD;
    this.absCos = Math.abs(Math.cos(angle));
    this.absSin = Math.abs(Math.sin(angle));

    const offset = Utils.getRandomInRange(-150, 0);
    const position = {
      x:
        initialPosition.x +
        (direction === 'left' ? -offset : offset) * this.absCos,
      y: initialPosition.y + offset * this.absSin,
    };

    this.position = { ...position };
    this.initialPosition = { ...position };
    this.color = emojis.length || svgIcon ? null : Utils.getRandomItem(colors);
    this.emoji = emojis.length ? Utils.getRandomItem(emojis) : null;
    this.svgIcon = null;

    if (svgIcon) {
      this.svgImage = new Image();
      this.svgImage.src = svgIcon;
      this.svgImage.onload = () => {
        this.svgIcon = this.svgImage;
      };
    }

    this.createdAt = Date.now();
    this.direction = direction;

    // State control for switching motion
    this.stoppedX = false;
    this.switchedToSlowY = false;
  }

  draw(context) {
    const { x, y } = this.position;
    const { x: radiusX, y: radiusY } = this.radius;
    const scale = window.devicePixelRatio;

    context.save();
    context.translate(scale * x, scale * y);

    if (this.svgIcon) {
      context.rotate(this.emojiRotationAngle);
      context.drawImage(
        this.svgIcon,
        -radiusX,
        -radiusY,
        radiusX * 2,
        radiusY * 2
      );
    } else if (this.color) {
      context.fillStyle = this.color;
      context.rotate(this.rotationAngle);
      const size = Math.min(radiusX, radiusY) * scale;
      context.fillRect(-size / 2, -size / 2, size, size);
    } else if (this.emoji) {
      context.font = `${radiusX * scale}px serif`;
      context.rotate(this.emojiRotationAngle);
      context.textAlign = 'center';
      context.fillText(this.emoji, 0, radiusY / 2);
    }

    context.restore();
  }

  updatePosition(deltaTime, currentTime) {
    const elapsed = currentTime - this.createdAt;

    // After 1.5s, stop horizontal movement
    if (elapsed < 1800) {
      if (this.speed.x > this.finalSpeedX) {
        this.speed.x -= this.dragCoefficient * deltaTime;
      }
      this.position.x +=
        this.speed.x *
        (this.direction === 'left' ? -this.absCos : this.absCos) *
        deltaTime;
    } else if (!this.stoppedX) {
      this.stoppedX = true;
    }

    // After 1.5s, switch to slow vertical descent
    if (elapsed < 1800) {
      this.position.y =
        this.initialPosition.y -
        (this.speed.y * this.absSin * elapsed -
          (0.00125 * Math.pow(elapsed, 2)) / 2);
    } else {
      if (!this.switchedToSlowY) {
        this.linearStartY = this.position.y;
        this.linearStartTime = currentTime;
        this.switchedToSlowY = true;
      }

      const slowElapsed = currentTime - this.linearStartTime;
      const slowSpeedY = 0.3 * Utils.getScaleFactor();
      this.position.y = this.linearStartY + slowSpeedY * slowElapsed;
    }

    // Wiggle effect
    if (!this.emoji && !this.svgIcon) {
      this.rotationSpeed -= 1e-5 * deltaTime;
      this.rotationSpeed = Math.max(this.rotationSpeed, 0);

      if (this.radiusYDirection === 'down') {
        this.radius.y -= deltaTime * this.rotationSpeed;
        if (this.radius.y <= 0) {
          this.radius.y = 0;
          this.radiusYDirection = 'up';
        }
      } else {
        this.radius.y += deltaTime * this.rotationSpeed;
        if (this.radius.y >= this.initialRadius) {
          this.radius.y = this.initialRadius;
          this.radiusYDirection = 'down';
        }
      }
    }
  }

  isVisible(canvasHeight) {
    return this.position.y > -100;
  }
}

const ConfettiCanvas = () => {
  const canvasRef = useRef(null);
  const confettiRef = useRef([]);
  const lastUpdated = useRef(Date.now());

  const addConfetti = (config = {}) => {
    const {
      confettiesNumber,
      confettiRadius,
      confettiColors,
      emojies,
      svgIcon,
    } = { ...defaultConfettiConfig, ...config };

    const baseX = window.innerWidth / 2;
    const baseY = window.innerHeight + 30;

    for (let i = 0; i < confettiesNumber; i++) {
      confettiRef.current.push(
        new Confetti({
          initialPosition: { x: baseX, y: baseY },
          direction: Utils.getRandomItem(['left', 'right']),
          radius: confettiRadius,
          colors: confettiColors,
          emojis: emojies,
          svgIcon,
        })
      );
    }
  };

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    resizeCanvas();
    addConfetti();

    let animationFrameId;

    const renderLoop = () => {
      const context = canvas.getContext('2d');
      const currentTime = Date.now();
      const deltaTime = currentTime - lastUpdated.current;
      lastUpdated.current = currentTime;

      context.clearRect(0, 0, canvas.width, canvas.height);

      confettiRef.current = confettiRef.current.filter((confetti) => {
        confetti.updatePosition(deltaTime, currentTime);
        confetti.draw(context);
        return confetti.isVisible(canvas.height);
      });

      if (confettiRef.current.length > 0) {
        animationFrameId = requestAnimationFrame(renderLoop);
      }
    };

    animationFrameId = requestAnimationFrame(renderLoop);

    const debouncedResize = Utils.debounce(resizeCanvas, 500);
    window.addEventListener('resize', debouncedResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    />
  );
};

export default ConfettiCanvas;
